import { useEffect, useRef } from "react";

const rules = [
  "同調しなくていい。",
  "合わせなくていい。",
  "癖でいい。",
  "未完成でいい。",
];

export default function RuleSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── DNA strand canvas — vertical left side ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* large ghost letter "R" in background */
      ctx.save();
      ctx.font = `${W * 0.55}px 'Helvetica Neue', sans-serif`;
      ctx.fillStyle = "rgba(255,220,140,0.018)";
      ctx.textAlign = "right";
      ctx.fillText("R", W * 0.98, H * 0.72);
      ctx.restore();

      /* vertical DNA helix on left */
      const cx = W * 0.06;
      const amp = W * 0.04;
      const ARMS = 2;
      const PTS = 60;

      for (let arm = 0; arm < ARMS; arm++) {
        const phaseOff = (arm / ARMS) * Math.PI * 2;
        ctx.beginPath();
        let first = true;
        for (let i = 0; i <= PTS; i++) {
          const frac = i / PTS;
          const theta = frac * Math.PI * 2 * 4 + t * 0.01 + phaseOff;
          const x = cx + Math.cos(theta) * amp;
          const y = frac * H;
          if (first) { ctx.moveTo(x, y); first = false; }
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = "rgba(255,210,120,0.14)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* rungs */
      for (let i = 0; i <= 24; i++) {
        const frac = i / 24;
        const theta = frac * Math.PI * 2 * 4 + t * 0.01;
        const y = frac * H;
        const x1 = cx + Math.cos(theta) * amp;
        const x2 = cx + Math.cos(theta + Math.PI) * amp;
        const a = Math.abs(Math.sin(theta)) * 0.1;
        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.strokeStyle = `rgba(255,220,140,${a})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* fractal expanding rings from bottom-right */
      for (let r = 0; r < 5; r++) {
        const pulse = ((t * 0.005 + r * 0.2) % 1);
        const radius = pulse * Math.min(W, H) * 0.7;
        const alpha = (1 - pulse) * 0.05;
        ctx.beginPath();
        ctx.arc(W * 0.9, H * 0.85, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const title = entry.target.querySelector(".rule-title");
            if (title) {
              setTimeout(() => {
                (title as HTMLElement).style.opacity = "1";
                (title as HTMLElement).style.transform = "translateY(0)";
                (title as HTMLElement).style.filter = "blur(0px)";
              }, 0);
            }
            const items = entry.target.querySelectorAll(".rule-item");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateX(0)";
              }, 500 + i * 220);
            });
            const close = entry.target.querySelector(".rule-close");
            if (close) {
              setTimeout(() => {
                (close as HTMLElement).style.opacity = "1";
              }, 500 + rules.length * 220 + 400);
            }
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="rule"
      ref={sectionRef}
      className="relative w-full flex items-center overflow-hidden"
      style={{ background: "#000000" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 py-24 md:py-40">
        {/* section label */}
        <div className="flex items-center gap-4 mb-12 md:mb-16">
          <div style={{ width: "24px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
          <p className="text-white/15 tracking-[0.5em] uppercase text-xs" style={{ fontFamily: "'Courier New', monospace" }}>
            rule
          </p>
        </div>

        <h2
          className="rule-title text-white font-black leading-none mb-16 md:mb-24"
          style={{
            fontSize: "clamp(36px, 10vw, 130px)",
            fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontWeight: 900,
            opacity: 0,
            transform: "translateY(24px)",
            filter: "blur(10px)",
            transition: "opacity 1.6s ease, transform 1.6s ease, filter 1.6s ease",
            letterSpacing: "-0.03em",
            textShadow: "0 0 80px rgba(255,220,140,0.08)",
          }}
        >
          THE<br />ONLY<br />RULE
        </h2>

        <div className="flex flex-col gap-0 max-w-lg">
          {rules.map((rule, i) => (
            <div
              key={i}
              className="rule-item flex items-center gap-4 md:gap-6 py-4 md:py-5 border-b"
              style={{
                borderColor: "rgba(255,220,140,0.06)",
                opacity: 0,
                transform: "translateX(-24px)",
                transition: "opacity 1.3s ease, transform 1.3s ease",
              }}
            >
              {/* index */}
              <span
                className="text-white/10 flex-shrink-0"
                style={{ fontFamily: "'Courier New', monospace", fontSize: "10px", letterSpacing: "0.2em" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              {/* dot */}
              <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(255,220,140,0.3)", flexShrink: 0 }} />
              <p
                className="text-white/75 font-light"
                style={{
                  fontSize: "clamp(18px, 2.4vw, 32px)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.04em",
                  lineHeight: 1.5,
                }}
              >
                {rule}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-14 md:mt-20 flex items-center gap-5">
          <div style={{ width: "1px", height: "40px", background: "linear-gradient(to bottom, rgba(255,220,140,0.25), transparent)" }} />
          <p
            className="rule-close text-white/25 font-light"
            style={{
              fontSize: "clamp(14px, 1.6vw, 20px)",
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              fontWeight: 300,
              letterSpacing: "0.06em",
              opacity: 0,
              transition: "opacity 1.6s ease",
            }}
          >
            整える場所じゃない。
          </p>
        </div>
      </div>
    </section>
  );
}
